"use client";

import Link from "next/link";
import { useRef } from "react";
import ContactForm from "../forms/ContactForm";
import { useLanguage } from "@/utils/LanguageContext";
import { useScrollFade } from "@/lib/MotionProvider";

const MAP_SEARCH_URL =
  "https://www.google.com/maps/search/?api=1&query=Lauriedstrasse+1,+6300+Zug,+Switzerland";

const PHONE_DISPLAY = "+39 376 252 7864";
const PHONE_HREF = "tel:+393762527864";
const WHATSAPP_URL = "https://wa.me/393762527864";
const WHATSAPP_DISPLAY = "wa.me/393762527864";
const TELEGRAM_URL = "https://t.me/Invtrondaogroup";

const ContactArea = () => {
  const { t, lang } = useLanguage();
  const heroRef = useRef<HTMLDivElement>(null);
  const methodsRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useScrollFade(heroRef, { offset: 36 });
  useScrollFade(methodsRef, { offset: 36 });
  useScrollFade(contentRef, { offset: 36 });

  const documentLinks = [
    {
      name: t("Whitepaper"),
      href: "/flipbook/whitepaper",
    },
    {
      name: t("Token Sale Terms"),
      href: `/${lang}/terms`,
    },
    {
      name: t("Pitch Deck"),
      href: "/flipbook/pitch-deck",
    },
    {
      name: t("Lightpaper"),
      href: "/flipbook/litepaper",
    },
  ];

  const handleDownloadAll = () => {
    if (typeof window === "undefined") {
      return;
    }

    const urls = [
      "https://invtron.com/Litepaper.pdf",
      "/White-Paper.pdf",
      "/Pitch-Deck.pdf",
    ];

    urls.forEach((url) => {
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "";
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    });
  };

  return (
    <section className="contact-page-shell" aria-labelledby="contact-page-title">
      <div className="container">
        <div ref={heroRef} className="contact-hero-shell">
          <span className="contact-eyebrow">{t("Have Any Questions?")}</span>
          <h1 id="contact-page-title" className="contact-page-title">
            {t("Contact")}
          </h1>
          <p className="contact-page-subtitle">{t("Get In Touch Now")}</p>
          <div className="contact-hero-meta" aria-label={t("Contact")}>
            <a href="mailto:info@invtron.com">info@invtron.com</a>
            <span aria-hidden="true">/</span>
            <a href={PHONE_HREF}>{PHONE_DISPLAY}</a>
          </div>
        </div>

        <div ref={methodsRef} className="contact-method-grid">
          <article className="contact-surface contact-method-card">
            <div className="contact-method-icon" aria-hidden="true">
              <i className="fas fa-map-marker-alt"></i>
            </div>
            <div className="contact-method-copy">
              <p className="contact-method-label">{t("Location")}</p>
              <address className="contact-method-value">
                <a href={MAP_SEARCH_URL} target="_blank" rel="noopener noreferrer">
                  Lauriedstrasse 1
                  <br />
                  6300 Zug, Switzerland
                </a>
              </address>
            </div>
          </article>

          <article className="contact-surface contact-method-card">
            <div className="contact-method-icon" aria-hidden="true">
              <i className="fas fa-envelope"></i>
            </div>
            <div className="contact-method-copy">
              <p className="contact-method-label">{t("Email")}</p>
              <p className="contact-method-value">
                <a href="mailto:info@invtron.com">info@invtron.com</a>
              </p>
            </div>
          </article>

          <article className="contact-surface contact-method-card">
            <div className="contact-method-icon" aria-hidden="true">
              <i className="fas fa-phone-alt"></i>
            </div>
            <div className="contact-method-copy">
              <p className="contact-method-label">{t("Mobile no")}</p>
              <p className="contact-method-value">
                <a href={PHONE_HREF}>{PHONE_DISPLAY}</a>
              </p>
            </div>
          </article>

          <article className="contact-surface contact-method-card">
            <div className="contact-method-icon" aria-hidden="true">
              <i className="fab fa-whatsapp"></i>
            </div>
            <div className="contact-method-copy">
              <p className="contact-method-label">{t("WhatsApp Chat")}</p>
              <p className="contact-method-value">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  {WHATSAPP_DISPLAY}
                </a>
              </p>
            </div>
          </article>
        </div>

        <div ref={contentRef} className="contact-content-grid">
          <section
            className="contact-surface contact-form-panel"
            aria-labelledby="contact-form-title"
          >
            <div className="contact-panel-heading">
              <p className="contact-panel-eyebrow">{t("Have Any Questions?")}</p>
              <h2 id="contact-form-title" className="contact-panel-title">
                {t("Send a message")}
              </h2>
            </div>
            <ContactForm />
          </section>

          <aside className="contact-sidebar">
            <section
              className="contact-surface contact-direct-panel"
              aria-labelledby="contact-direct-title"
            >
              <div className="contact-panel-heading">
                <p className="contact-panel-eyebrow">{t("Get In Touch Now")}</p>
                <h2 id="contact-direct-title" className="contact-panel-title">
                  {t("Contact")}
                </h2>
              </div>

              <div className="contact-direct-list">
                <a href={PHONE_HREF} className="contact-direct-item">
                  <span className="contact-direct-icon" aria-hidden="true">
                    <i className="fas fa-phone-alt"></i>
                  </span>
                  <span className="contact-direct-copy">
                    <span className="contact-direct-label">{t("Mobile no")}</span>
                    <span className="contact-direct-value">{PHONE_DISPLAY}</span>
                  </span>
                </a>

                <a href="mailto:info@invtron.com" className="contact-direct-item">
                  <span className="contact-direct-icon" aria-hidden="true">
                    <i className="fas fa-envelope"></i>
                  </span>
                  <span className="contact-direct-copy">
                    <span className="contact-direct-label">{t("Email")}</span>
                    <span className="contact-direct-value">info@invtron.com</span>
                  </span>
                </a>

                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-direct-item"
                >
                  <span className="contact-direct-icon" aria-hidden="true">
                    <i className="fab fa-whatsapp"></i>
                  </span>
                  <span className="contact-direct-copy">
                    <span className="contact-direct-label">{t("WhatsApp Chat")}</span>
                    <span className="contact-direct-value">{WHATSAPP_DISPLAY}</span>
                  </span>
                </a>
              </div>

              <div className="contact-action-group">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-action-btn contact-action-btn--whatsapp"
                >
                  <i className="fab fa-whatsapp" aria-hidden="true"></i>
                  <span>{t("WhatsApp Chat")}</span>
                </a>
                <a
                  href={TELEGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-action-btn contact-action-btn--telegram"
                >
                  <i className="fab fa-telegram-plane" aria-hidden="true"></i>
                  <span>{t("Join Telegram")}</span>
                </a>
              </div>
            </section>

            <section
              className="contact-surface contact-docs-panel"
              aria-labelledby="contact-docs-title"
            >
              <div className="contact-panel-heading">
                <p className="contact-panel-eyebrow">{t("Read Documents")}</p>
                <h2 id="contact-docs-title" className="contact-panel-title">
                  {t("Read Documents")}
                </h2>
              </div>

              <div className="contact-resource-list">
                {documentLinks.map((doc, index) => (
                  <Link
                    key={doc.name}
                    href={doc.href}
                    prefetch={false}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-resource-link"
                  >
                    <span className="contact-resource-card">
                      <span className="contact-resource-icon" aria-hidden="true">
                        <i className="fas fa-file-pdf"></i>
                      </span>
                      <span className="contact-resource-copy">
                        <span className="contact-resource-index">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <span className="contact-resource-name">{doc.name}</span>
                      </span>
                      <span className="contact-resource-arrow" aria-hidden="true">
                        <i className="fas fa-arrow-right"></i>
                      </span>
                    </span>
                  </Link>
                ))}
              </div>

              <div className="contact-docs-footer">
                <button
                  type="button"
                  className="contact-download-btn"
                  onClick={handleDownloadAll}
                >
                  <i className="fas fa-download" aria-hidden="true"></i>
                  <span>{t("Download All")}</span>
                </button>
              </div>
            </section>
          </aside>
        </div>
      </div>

      <style jsx>{`
        .contact-page-shell {
          position: relative;
          overflow: hidden;
          padding: clamp(152px, 18vw, 210px) 0 clamp(96px, 12vw, 140px);
          background:
            radial-gradient(760px 380px at 0% 8%, rgba(87, 183, 255, 0.14), transparent 60%),
            radial-gradient(760px 340px at 100% 0%, rgba(104, 83, 247, 0.16), transparent 62%),
            linear-gradient(180deg, #07111c 0%, #081722 100%);
        }

        .contact-page-shell::before,
        .contact-page-shell::after {
          content: "";
          position: absolute;
          inset: auto;
          pointer-events: none;
          z-index: 0;
        }

        .contact-page-shell::before {
          top: 120px;
          right: -120px;
          width: 340px;
          height: 340px;
          border-radius: 999px;
          background: radial-gradient(circle, rgba(61, 154, 255, 0.18), transparent 68%);
          filter: blur(10px);
        }

        .contact-page-shell::after {
          left: -140px;
          bottom: 80px;
          width: 380px;
          height: 380px;
          border-radius: 999px;
          background: radial-gradient(circle, rgba(106, 83, 247, 0.18), transparent 70%);
          filter: blur(16px);
        }

        .container {
          position: relative;
          z-index: 1;
        }

        .contact-hero-shell {
          max-width: 760px;
          margin: 0 auto;
          text-align: center;
        }

        .contact-eyebrow,
        .contact-panel-eyebrow {
          margin: 0 0 0.85rem;
          color: rgba(173, 213, 231, 0.8);
          font-size: 0.9rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          text-transform: uppercase;
        }

        .contact-page-title,
        .contact-panel-title {
          margin: 0;
          color: #d9ebfa;
          font-family: "Lexend Deca", var(--tg-heading-font-family);
          letter-spacing: -0.05em;
        }

        .contact-page-title {
          font-size: clamp(2.6rem, 2.2rem + 1.6vw, 4.1rem);
          line-height: 1.02;
        }

        .contact-page-subtitle {
          margin: 1rem 0 0;
          color: rgba(221, 235, 247, 0.82);
          font-size: clamp(1.02rem, 0.96rem + 0.25vw, 1.18rem);
          line-height: 1.8;
        }

        .contact-hero-meta {
          margin-top: 1.5rem;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          gap: 0.9rem;
          color: rgba(210, 231, 245, 0.78);
          font-size: 0.96rem;
        }

        .contact-hero-meta a {
          color: #f1f7ff;
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .contact-hero-meta a:hover,
        .contact-hero-meta a:focus-visible {
          color: #80beff;
        }

        .contact-method-grid {
          margin-top: clamp(34px, 5vw, 52px);
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 1.2rem;
        }

        .contact-content-grid {
          margin-top: clamp(26px, 4vw, 38px);
          display: grid;
          grid-template-columns: minmax(0, 1.3fr) minmax(320px, 0.82fr);
          gap: 1.5rem;
          align-items: start;
        }

        .contact-sidebar {
          display: grid;
          gap: 1.5rem;
        }

        .contact-surface {
          position: relative;
          overflow: hidden;
          border-radius: 24px;
          border: 1px solid rgba(189, 213, 231, 0.12);
          background:
            linear-gradient(160deg, rgba(9, 28, 53, 0.84), rgba(11, 35, 64, 0.62)),
            radial-gradient(760px 280px at 0% 0%, rgba(88, 170, 255, 0.12), transparent 58%);
          box-shadow:
            0 18px 42px rgba(3, 19, 43, 0.32),
            inset 0 1px 0 rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(14px) saturate(135%);
          -webkit-backdrop-filter: blur(14px) saturate(135%);
        }

        .contact-surface::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(173, 215, 255, 0.08), transparent 40%);
          pointer-events: none;
        }

        .contact-method-card {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          min-height: 100%;
          padding: 1.35rem 1.35rem 1.45rem;
        }

        .contact-method-icon {
          position: relative;
          z-index: 1;
          flex: 0 0 54px;
          width: 54px;
          height: 54px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 16px;
          color: #eaf5ff;
          font-size: 1.15rem;
          background: linear-gradient(160deg, rgba(255, 255, 255, 0.16), rgba(255, 255, 255, 0.08));
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18);
        }

        .contact-method-copy {
          position: relative;
          z-index: 1;
          min-width: 0;
        }

        .contact-method-label {
          margin: 0 0 0.4rem;
          color: rgba(173, 213, 231, 0.78);
          font-size: 0.84rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        .contact-method-value {
          margin: 0;
          color: #eaf5ff;
          font-size: 1rem;
          line-height: 1.72;
          font-style: normal;
          overflow-wrap: anywhere;
        }

        .contact-method-value a {
          color: inherit;
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .contact-method-value a:hover,
        .contact-method-value a:focus-visible {
          color: #80beff;
        }

        .contact-form-panel,
        .contact-direct-panel,
        .contact-docs-panel {
          padding: clamp(1.35rem, 2.3vw, 1.9rem);
        }

        .contact-direct-panel,
        .contact-docs-panel {
          display: grid;
        }

        .contact-panel-heading {
          position: relative;
          z-index: 1;
          margin-bottom: 1.35rem;
        }

        .contact-panel-eyebrow {
          margin-bottom: 0.55rem;
          font-size: 0.8rem;
        }

        .contact-panel-title {
          font-size: clamp(1.6rem, 1.48rem + 0.35vw, 1.95rem);
          line-height: 1.1;
        }

        .contact-direct-list {
          position: relative;
          z-index: 1;
          display: grid;
          gap: 0.75rem;
        }

        .contact-direct-item {
          display: flex;
          align-items: center;
          gap: 0.9rem;
          min-height: 78px;
          padding: 0.95rem 1rem;
          border-radius: 20px;
          border: 1px solid rgba(189, 213, 231, 0.12);
          background:
            linear-gradient(160deg, rgba(12, 33, 59, 0.8), rgba(9, 26, 46, 0.62)),
            radial-gradient(420px 220px at 0% 0%, rgba(88, 170, 255, 0.11), transparent 58%);
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.05),
            0 10px 24px rgba(4, 14, 32, 0.16);
          color: #eef7ff;
          text-decoration: none;
          transition:
            transform 0.2s ease,
            border-color 0.2s ease,
            box-shadow 0.2s ease;
        }

        .contact-direct-item:hover,
        .contact-direct-item:focus-visible {
          transform: translateY(-2px);
          border-color: rgba(120, 185, 255, 0.28);
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.06),
            0 16px 30px rgba(4, 14, 32, 0.22);
        }

        .contact-direct-icon {
          flex: 0 0 48px;
          width: 48px;
          height: 48px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 16px;
          background:
            linear-gradient(160deg, rgba(84, 167, 255, 0.28), rgba(255, 255, 255, 0.06)),
            rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(173, 215, 255, 0.16);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.16);
          color: #edf7ff;
        }

        .contact-direct-copy {
          min-width: 0;
          display: grid;
          gap: 0.24rem;
        }

        .contact-direct-label {
          color: rgba(173, 213, 231, 0.7);
          font-size: 0.76rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }

        .contact-direct-value {
          color: #eef7ff;
          font-size: 0.98rem;
          font-weight: 600;
          line-height: 1.4;
          overflow-wrap: anywhere;
        }

        .contact-action-group {
          position: relative;
          z-index: 1;
          display: grid;
          gap: 0.8rem;
          margin-top: 1.15rem;
          padding-top: 1.15rem;
          border-top: 1px solid rgba(189, 213, 231, 0.12);
        }

        .contact-action-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.65rem;
          min-height: 56px;
          padding: 0.95rem 1.35rem;
          border-radius: 999px;
          text-decoration: none;
          font-weight: 700;
          letter-spacing: 0.01em;
          transition:
            transform 0.2s ease,
            box-shadow 0.2s ease,
            border-color 0.2s ease,
            background 0.2s ease;
        }

        .contact-action-btn:hover,
        .contact-action-btn:focus-visible {
          transform: translateY(-1px);
        }

        .contact-action-btn--whatsapp {
          border: 1px solid rgba(73, 214, 138, 0.28);
          background:
            linear-gradient(135deg, rgba(31, 163, 90, 0.96), rgba(20, 110, 66, 0.95)),
            rgba(12, 43, 30, 0.92);
          box-shadow:
            0 14px 30px rgba(4, 14, 32, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.14);
          color: #f4fffa;
        }

        .contact-action-btn--whatsapp:hover,
        .contact-action-btn--whatsapp:focus-visible {
          border-color: rgba(111, 230, 165, 0.4);
          box-shadow:
            0 18px 36px rgba(4, 14, 32, 0.34),
            0 0 0 1px rgba(111, 230, 165, 0.14);
        }

        .contact-action-btn--telegram {
          border: 1px solid rgba(120, 185, 255, 0.3);
          background:
            linear-gradient(135deg, rgba(34, 106, 184, 0.96), rgba(11, 54, 103, 0.95)),
            rgba(10, 30, 58, 0.92);
          box-shadow:
            0 14px 30px rgba(4, 14, 32, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.16);
          color: #eef7ff;
        }

        .contact-action-btn--telegram:hover,
        .contact-action-btn--telegram:focus-visible {
          border-color: rgba(153, 206, 255, 0.46);
          box-shadow:
            0 18px 38px rgba(4, 14, 32, 0.4),
            0 0 0 1px rgba(146, 204, 255, 0.18);
        }

        .contact-resource-list {
          position: relative;
          z-index: 1;
          display: grid;
          gap: 0.75rem;
        }

        :global(.contact-resource-link) {
          display: block;
          text-decoration: none;
        }

        :global(.contact-resource-link:focus-visible) {
          outline: none;
        }

        .contact-resource-card {
          display: grid;
          grid-template-columns: auto minmax(0, 1fr) auto;
          align-items: center;
          gap: 0.9rem;
          min-height: 82px;
          padding: 0.95rem 1rem;
          border-radius: 20px;
          border: 1px solid rgba(189, 213, 231, 0.12);
          background:
            linear-gradient(160deg, rgba(12, 33, 59, 0.82), rgba(9, 26, 46, 0.64)),
            radial-gradient(460px 220px at 0% 0%, rgba(88, 170, 255, 0.12), transparent 58%);
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.05),
            0 12px 28px rgba(4, 14, 32, 0.18);
          text-decoration: none;
          transition:
            transform 0.2s ease,
            border-color 0.2s ease,
            background 0.2s ease,
            box-shadow 0.2s ease;
        }

        :global(.contact-resource-link:hover) .contact-resource-card,
        :global(.contact-resource-link:focus-visible) .contact-resource-card {
          transform: translateY(-2px);
          border-color: rgba(120, 185, 255, 0.3);
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.06),
            0 18px 34px rgba(4, 14, 32, 0.24);
        }

        .contact-resource-icon {
          width: 50px;
          height: 50px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 16px;
          background:
            linear-gradient(160deg, rgba(84, 167, 255, 0.28), rgba(255, 255, 255, 0.06)),
            rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(173, 215, 255, 0.16);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.16);
          color: #edf7ff;
          font-size: 1rem;
        }

        .contact-resource-copy {
          min-width: 0;
          display: grid;
          gap: 0.3rem;
        }

        .contact-resource-index {
          color: rgba(173, 213, 231, 0.6);
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          text-transform: uppercase;
        }

        .contact-resource-name {
          color: #eef7ff;
          font-size: 1rem;
          font-weight: 600;
          line-height: 1.25;
        }

        .contact-resource-arrow {
          width: 34px;
          height: 34px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          border: 1px solid rgba(173, 215, 255, 0.12);
          background: rgba(255, 255, 255, 0.04);
          color: rgba(219, 238, 251, 0.72);
          transition:
            transform 0.2s ease,
            border-color 0.2s ease,
            background 0.2s ease,
            color 0.2s ease;
        }

        :global(.contact-resource-link:hover) .contact-resource-arrow,
        :global(.contact-resource-link:focus-visible) .contact-resource-arrow {
          transform: translateX(2px);
          border-color: rgba(153, 206, 255, 0.28);
          background: rgba(120, 185, 255, 0.12);
          color: #eef7ff;
        }

        .contact-docs-footer {
          position: relative;
          z-index: 1;
          margin-top: 1.15rem;
          padding-top: 1.15rem;
          border-top: 1px solid rgba(189, 213, 231, 0.12);
        }

        .contact-download-btn {
          position: relative;
          z-index: 1;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          gap: 0.65rem;
          min-height: 56px;
          padding: 0.95rem 1.35rem;
          border: 1px solid rgba(120, 185, 255, 0.3);
          border-radius: 999px;
          background:
            linear-gradient(135deg, rgba(34, 106, 184, 0.96), rgba(11, 54, 103, 0.95)),
            rgba(10, 30, 58, 0.92);
          box-shadow:
            0 14px 30px rgba(4, 14, 32, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.16);
          color: #eef7ff;
          font-weight: 700;
          letter-spacing: 0.01em;
          cursor: pointer;
          transition:
            transform 0.2s ease,
            box-shadow 0.2s ease,
            border-color 0.2s ease,
            background 0.2s ease;
        }

        .contact-download-btn:hover,
        .contact-download-btn:focus-visible {
          transform: translateY(-1px);
          border-color: rgba(153, 206, 255, 0.46);
          background:
            linear-gradient(135deg, rgba(39, 118, 207, 0.98), rgba(14, 68, 129, 0.96)),
            rgba(10, 30, 58, 0.96);
          box-shadow:
            0 18px 38px rgba(4, 14, 32, 0.4),
            0 0 0 1px rgba(146, 204, 255, 0.18);
        }

        .contact-download-btn:focus-visible {
          outline: none;
        }

        @media (max-width: 1199px) {
          .contact-content-grid {
            grid-template-columns: minmax(0, 1fr) minmax(300px, 0.9fr);
          }
        }

        @media (max-width: 991px) {
          .contact-page-shell {
            padding-top: 148px;
          }

          .contact-method-grid,
          .contact-content-grid {
            grid-template-columns: minmax(0, 1fr);
          }
        }

        @media (max-width: 767px) {
          .contact-hero-meta {
            gap: 0.65rem;
            font-size: 0.92rem;
          }

          .contact-method-card {
            padding: 1.2rem;
          }

          .contact-form-panel,
          .contact-direct-panel,
          .contact-docs-panel {
            padding: 1.2rem;
          }

          .contact-direct-item,
          .contact-resource-card {
            min-height: 74px;
            padding: 0.9rem;
          }

          .contact-direct-icon,
          .contact-resource-icon {
            width: 46px;
            height: 46px;
          }
        }

        @media (max-width: 575px) {
          .contact-hero-meta {
            flex-direction: column;
          }

          .contact-hero-meta span {
            display: none;
          }

          .contact-resource-card {
            grid-template-columns: auto minmax(0, 1fr);
          }

          .contact-resource-arrow {
            display: none;
          }
        }
      `}</style>
    </section>
  );
};

export default ContactArea;
